import type { MessageKey } from '@bookingdei/i18n/message-key';
import type { NavIconName } from './nav-icon';

// The admin console navigation model (PRD v2.5 §7.3.4, DC90). One named list
// (rule 7.1-2 bans utils/) consumed by the desktop rail, the mobile section-chip
// row, the ⌘K palette's "Go to" group and the section route guard, so all four
// stay in sync. Order and grouping follow the design rail (`NAV`, super-admin
// `.dc.html` D:8428) MINUS the sections DR-43 defers — Waitlist, Account requests,
// Ad review, Ads manager, Ads policy, Help centre get no entry at all.
//
// Section access is the fixed five-role matrix (FR-A15, DB-3: app configuration,
// not a table; no custom roles, DR-43). Values are the design `adminRoleAccess`
// (gap report A.3) for the kept sections. The rail only hides what a grant cannot
// reach — enforcement stays server-side (every admin route is grant-gated).
export type SectionId =
  | 'overview'
  | 'approvals'
  | 'services'
  | 'bookings'
  | 'offers'
  | 'disputes'
  | 'reviews'
  | 'merchants'
  | 'businesses'
  | 'customers'
  | 'pricing'
  | 'payments'
  | 'reports'
  | 'templates'
  | 'categories'
  | 'locations'
  | 'cms'
  | 'maps'
  | 'notifications'
  | 'roles';

export type NavGroupId = 'main' | 'ops' | 'people' | 'revenue' | 'platform' | 'system';

/** The five fixed admin grants (contract `auth.yaml#/Role` admin grants, DB-3). */
export type AdminGrant = 'super_admin' | 'operations' | 'finance' | 'support' | 'marketing';

export interface NavItem {
  section: SectionId;
  to: string;
  key: MessageKey;
  icon: NavIconName;
  group: NavGroupId;
}

// Rail groups in design order. `main` holds Overview and never shows a header
// (design: `header: gid !== 'main'`), so it is also never collapsible.
export const navGroups: { group: NavGroupId; label: MessageKey }[] = [
  { group: 'main', label: 'admin.nav.group.overview' },
  { group: 'ops', label: 'admin.nav.group.operations' },
  { group: 'people', label: 'admin.nav.group.people' },
  { group: 'revenue', label: 'admin.nav.group.revenue' },
  { group: 'platform', label: 'admin.nav.group.platform' },
  { group: 'system', label: 'admin.nav.group.system' },
];

export const navItems: NavItem[] = [
  { section: 'overview', to: '/', key: 'admin.nav.overview', icon: 'grid', group: 'main' },

  { section: 'approvals', to: '/approvals', key: 'admin.nav.approvals', icon: 'clipboard', group: 'ops' },
  // Listings sit with the work on them (DR-78: People are merchants and customers).
  { section: 'businesses', to: '/businesses', key: 'admin.nav.businesses', icon: 'store', group: 'ops' },
  { section: 'services', to: '/services', key: 'admin.nav.services', icon: 'layers', group: 'ops' },
  { section: 'bookings', to: '/bookings', key: 'admin.nav.bookings', icon: 'calendar', group: 'ops' },
  { section: 'offers', to: '/offers', key: 'admin.nav.offers', icon: 'tag', group: 'ops' },
  { section: 'disputes', to: '/disputes', key: 'admin.nav.disputes', icon: 'scale', group: 'ops' },
  { section: 'reviews', to: '/reviews', key: 'admin.nav.reviews', icon: 'star', group: 'ops' },

  // DR-78: Merchants are PEOPLE (accounts); Businesses are their listings.
  { section: 'merchants', to: '/merchants', key: 'admin.nav.merchants', icon: 'user', group: 'people' },
  { section: 'customers', to: '/customers', key: 'admin.nav.customers', icon: 'user', group: 'people' },

  { section: 'pricing', to: '/pricing', key: 'admin.nav.pricing', icon: 'currency', group: 'revenue' },
  { section: 'payments', to: '/payments', key: 'admin.nav.payments', icon: 'swap', group: 'revenue' },
  { section: 'reports', to: '/reports', key: 'admin.nav.reports', icon: 'chart', group: 'revenue' },

  { section: 'templates', to: '/templates', key: 'admin.nav.templates', icon: 'layout', group: 'platform' },
  { section: 'categories', to: '/categories', key: 'admin.nav.categories', icon: 'folder', group: 'platform' },
  { section: 'locations', to: '/locations', key: 'admin.nav.locations', icon: 'pin', group: 'platform' },
  { section: 'cms', to: '/cms', key: 'admin.nav.cms', icon: 'pen', group: 'platform' },
  // Maps: the listing-map provider settings; every role sees the usage dashboard
  // (read-only), only super_admin edits (the API enforces it).
  { section: 'maps', to: '/maps', key: 'admin.nav.maps', icon: 'pin', group: 'platform' },

  { section: 'notifications', to: '/notifications', key: 'admin.nav.notifications', icon: 'bell', group: 'system' },
  { section: 'roles', to: '/roles', key: 'admin.nav.roles', icon: 'shield', group: 'system' },
];

const ALL_SECTIONS: SectionId[] = navItems.map((i) => i.section);

// Which sections each grant unlocks — design `adminRoleAccess` (gap report A.3)
// for the 19 kept sections (DR-78 split Merchants into people + Businesses). A member's visible nav is the union across grants.
export const sectionAccess: Record<AdminGrant, SectionId[]> = {
  super_admin: ALL_SECTIONS,
  operations: [
    'overview', 'approvals', 'services', 'bookings', 'offers', 'disputes', 'reviews',
    'merchants', 'businesses', 'customers', 'reports', 'templates', 'categories', 'locations', 'maps', 'notifications',
  ],
  finance: ['overview', 'merchants', 'businesses', 'pricing', 'payments', 'reports', 'maps', 'notifications'],
  support: ['overview', 'services', 'bookings', 'disputes', 'reviews', 'merchants', 'businesses', 'customers', 'maps', 'notifications'],
  marketing: ['overview', 'offers', 'reports', 'cms', 'maps', 'notifications'],
};

/** The sections a set of grants unlocks. Unknown grants unlock nothing. */
export function sectionsForGrants(grants: readonly string[]): Set<SectionId> {
  return new Set(grants.flatMap((g) => sectionAccess[g as AdminGrant] ?? []));
}

export function canAccess(grants: readonly string[], section: SectionId): boolean {
  return sectionsForGrants(grants).has(section);
}

/**
 * The nav items a set of grants unlocks, in design order. Overview is always
 * present — every role has it, and an admin with no recognised grant still gets a
 * landing page (the rest of the rail stays hidden; the API refuses their calls).
 */
export function navItemsForGrants(grants: readonly string[]): NavItem[] {
  const unlocked = sectionsForGrants(grants);
  unlocked.add('overview');
  return navItems.filter((item) => unlocked.has(item.section));
}

export function navItemFor(section: SectionId): NavItem {
  const item = navItems.find((i) => i.section === section);
  if (!item) throw new Error(`Unknown section ${section}`);
  return item;
}

/** The nav item whose route owns a pathname (longest prefix; `/` only exactly). */
export function navItemForPath(pathname: string): NavItem | undefined {
  if (pathname === '/' || pathname === '') return navItemFor('overview');
  return navItems.find((i) => i.to !== '/' && (pathname === i.to || pathname.startsWith(`${i.to}/`)));
}
